import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Alert,
} from "@mui/material";
import VenueScheduleCalendar from "../calendar/VenueScheduleCalendar";
import axiosInstance from "../AxiosInstance";
import StateSelect from "../common/StateSelect";

const VenueDialog = ({
  open,
  handleClose,
  isEditMode,
  currentVenue,
  originalVenueId,
  handleChange,
  handleSave,
  handleInsert,
  handleDelete,
  timeZones,
  instructors,
  setInstructors,
  icpisManager,
  userRole,
  icpisname,
  isDisabled,
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState("");
  const [idValid, setIdValid] = useState(true); // State for ID validation
  const [icpisManagers, setIcpisManagers] = useState([]);
  const [showIdChangeWarning, setShowIdChangeWarning] = useState(false);
  const fullAddressRegex =
    /^\s*[^,]+(?:,\s*[^,]+)?\s*,\s*[A-Za-z .'-]+,\s*[A-Za-z]{2}\s+\d{5}(?:-\d{4})?\s*$/;
  const isAddressValid = fullAddressRegex.test((currentVenue.address || "").trim());
  const showAddressError = !isAddressValid;
  const stateToTimeZoneMap = {
    AK: "AKST",
    AL: "CST",
    AR: "CST",
    AZ: "MST",
    CA: "PST",
    CO: "MST",
    CT: "EST",
    DC: "EST",
    DE: "EST",
    FL: "EST",
    GA: "EST",
    HI: "UTC",
    IA: "CST",
    ID: "MST",
    IL: "CST",
    IN: "EST",
    KS: "CST",
    KY: "EST",
    LA: "CST",
    MA: "EST",
    MD: "EST",
    ME: "EST",
    MI: "EST",
    MN: "CST",
    MO: "CST",
    MS: "CST",
    MT: "MST",
    NC: "EST",
    ND: "CST",
    NE: "CST",
    NH: "EST",
    NJ: "EST",
    NM: "MST",
    NV: "PST",
    NY: "EST",
    OH: "EST",
    OK: "CST",
    OR: "PST",
    PA: "EST",
    RI: "EST",
    SC: "EST",
    SD: "CST",
    TN: "CST",
    TX: "CST",
    UT: "MST",
    VA: "EST",
    VT: "EST",
    WA: "PST",
    WI: "CST",
    WV: "EST",
    WY: "MST",
  };

  useEffect(() => {
    const setFieldIfChanged = (name, value) => {
      if ((currentVenue?.[name] || "") !== value) {
        handleChange({ target: { name, value } });
      }
    };

    if (!isAddressValid) {
      setFieldIfChanged("city", "");
      setFieldIfChanged("state", "");
      setFieldIfChanged("timeZone", "");
      return;
    }

    const addressParts = (currentVenue.address || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (addressParts.length < 3) return;

    const city = addressParts[addressParts.length - 2] || "";
    const stateZipPart = addressParts[addressParts.length - 1] || "";
    const stateZipMatch = stateZipPart.match(
      /^([A-Za-z]{2})\s+\d{5}(?:-\d{4})?$/,
    );

    if (!stateZipMatch) return;

    const state = stateZipMatch[1].toUpperCase();
    const timeZone = stateToTimeZoneMap[state] || "UTC";

    setFieldIfChanged("city", city);
    setFieldIfChanged("state", state);
    setFieldIfChanged("timeZone", timeZone);
  }, [currentVenue, isAddressValid, handleChange]);

  // 检测 ID 是否发生变化
  const isIdChanged =
    isEditMode && originalVenueId && currentVenue.id !== originalVenueId;

  useEffect(() => {
    if (userRole === "ROLE_ICPIE") {
      axiosInstance
        .get("/icpie/allicpiamanger")
        .then((response) => {
          setIcpisManagers(response.data); // ["Daniel", "Andy", "Fisher", ...]
        })
        .catch((error) => {
          console.error("Failed to load ICPIS Manager list", error);
        });
    }
  }, [userRole]);
  const checkIdExists = async () => {
    try {
      setIsChecking(true);
      const response = await axiosInstance.get(
        `/venue/checkIdExists/${currentVenue.id}`,
      );
      setIsChecking(false);
      return response.data.exists; // Assumes API returns { exists: true/false }
    } catch (error) {
      setIsChecking(false);
      console.error("Error checking venue ID", error);
      setError("Error checking venue ID");
      return false;
    }
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" && isEditMode) {
      if (!isAddressValid) {
        window.alert(
          "Full Address format is invalid. Please use: address line1, address line2(optional), city, ST zipcode",
        );
        return;
      }
      handleSave();
      return;
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? "Edit Venue" : "Add a Venue"}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isEditMode
            ? "Edit the information of the venue."
            : "Add the information of the venue"}
        </DialogContentText>

        <Grid container spacing={-5}></Grid>
        <Grid item xs={6}>
          <TextField
            margin="dense"
            label="ID"
            type="text"
            fullWidth
            name="id"
            value={currentVenue.id}
            onChange={handleChange}
            helperText={
              isEditMode ? "Editing ID will update all related records" : ""
            }
          />
          {isIdChanged && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              ⚠️ You are changing the Venue ID from{" "}
              <strong>{originalVenueId}</strong> to{" "}
              <strong>{currentVenue.id}</strong>. This will update all related
              records including: Instructor associations, Venue schedules, and
              Course schedules.
            </Alert>
          )}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Full Address"
              type="text"
              fullWidth
              name="address"
              value={currentVenue.address}
              onChange={handleChange}
              error={showAddressError}
              helperText={
                showAddressError
                  ? "Use format: address line1, address line2(optional), city, ST zipcode"
                  : "Format: address line1, address line2(optional), city, ST zipcode"
              }
              placeholder="123 Main St, Suite 200, Los Angeles, CA 90001"
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl margin="dense" fullWidth>
              <InputLabel id="time-zone-label">Time Zone</InputLabel>
              <Select
                labelId="time-zone-label"
                name="timeZone"
                value={currentVenue.timeZone}
                onChange={handleChange}
                label="Time Zone"
                disabled
              >
                {timeZones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <StateSelect
              label="State"
              value={currentVenue.state}
              onChange={(val) =>
                handleChange({ target: { name: "state", value: val } })
              }
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="dense" fullWidth>
              <InputLabel id="instructors-label">Instructors</InputLabel>
              <Select
                labelId="instructors-label"
                name="instructors"
                multiple
                value={
                  Array.isArray(currentVenue.instructors)
                    ? currentVenue.instructors.map((i) => i.id)
                    : []
                }
                onChange={handleChange}
                label="Instructors"
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const instructor = instructors.find((i) => i.id === id);
                      //console.log("current venues instructors", currentVenue.instructors);
                      //console.log('Selected ID:', id);
                      //console.log('Found instructor:', instructor);

                      return instructor ? instructor.fullname : ""; // 如果找到对应的instructor，显示其fullname
                    })
                    .join(", ")
                } // 使用逗号分隔多个instructor的fullname
              >
                {/* 对 instructors 按 fullname 从A到Z排序 */}
                {instructors
                  .slice() // 复制数组，避免修改原始数据
                  .sort((a, b) => a.fullname.localeCompare(b.fullname)) // 按 fullname 排序
                  .map((instructor) => (
                    <MenuItem key={instructor.id} value={instructor.id}>
                      {instructor.fullname}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="City"
              type="text"
              fullWidth
              name="city"
              value={currentVenue.city}
              onChange={handleChange}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Site"
              type="number"
              fullWidth
              name="site"
              value={currentVenue.site ?? ""}
              onChange={handleChange}
              inputProps={{ inputMode: "numeric" }}
              helperText="Optional number to distinguish multiple venues in the same city"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="dense" fullWidth>
              <InputLabel id="icpis-manager-label">ICPIS Manager</InputLabel>
              <Select
                labelId="icpis-manager-label"
                name="icpisManager"
                value={currentVenue.icpisManager}
                onChange={handleChange}
                label="ICPIS Manager"
                disabled={userRole !== "ROLE_ICPIE"}
              >
                {userRole === "ROLE_ICPIE" ? (
                  icpisManagers.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={icpisname}>{icpisname}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Cancellation Policy"
              type="text"
              fullWidth
              name="cancellationPolicy"
              value={currentVenue.cancellationPolicy}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Payment Mode"
              type="text"
              fullWidth
              name="paymentMode"
              value={currentVenue.paymentMode}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Nonrefundable Fee"
              type="number"
              fullWidth
              name="nonrefundableFee"
              value={currentVenue.nonrefundableFee}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Fob Key"
              type="text"
              fullWidth
              name="fobKey"
              value={currentVenue.fobKey}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Deposit"
              type="text"
              fullWidth
              name="deposit"
              value={currentVenue.deposit}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Membership Fee"
              type="number"
              fullWidth
              name="membershipFee"
              value={currentVenue.membershipFee}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Usage Fee"
              type="number"
              fullWidth
              name="usageFee"
              value={currentVenue.usageFee}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Refundable Status"
              type="text"
              fullWidth
              name="refundableStatus"
              value={currentVenue.refundableStatus}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Book Method"
              type="text"
              fullWidth
              name="bookMethod"
              value={currentVenue.bookMethod}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Registration Link"
              type="text"
              fullWidth
              name="registrationLink"
              value={currentVenue.registrationLink}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="CPR Price"
              type="number"
              fullWidth
              name="cprPrice"
              value={currentVenue.cprPrice}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="BLS Price"
              type="number"
              fullWidth
              name="blsPrice"
              value={currentVenue.blsPrice}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="CPR Adult Price"
              type="number"
              fullWidth
              name="cpradultPrice"
              value={currentVenue.cpradultPrice}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="CPR Instructor Price"
              type="number"
              fullWidth
              name="cprinstructorPrice"
              value={currentVenue.cprinstructorPrice}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="CPR Skill Price"
              type="number"
              fullWidth
              name="cprskillPrice"
              value={currentVenue.cprskillPrice}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="How Many Times Biweekly(Includes All Classes)"
              type="number"
              fullWidth
              name="adFrequencyValue"
              value={currentVenue.adFrequencyValue}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="The reason of frequency"
              type="text"
              fullWidth
              name="adFrequencyReason"
              value={currentVenue.adFrequencyReason}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl margin="dense" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                labelId="select-label"
                value={currentVenue.venueStatus}
                onChange={handleChange}
                label="Select Option"
                name="venueStatus"
              >
                <MenuItem value="NORMAL">NORMAL</MenuItem>
                <MenuItem value="INSTRUCTORISSUE">INSTRUCTOR ISSUE</MenuItem>
                <MenuItem value="VENUEISSUE">VENUE ISSUE</MenuItem>
                <MenuItem value="CLOSED">CLOSED</MenuItem>
                <MenuItem value="INVESTIGATION">INVESTIGATION</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {isEditMode && (
          <VenueScheduleCalendar
            venueId={currentVenue.id}
            currentVenue={currentVenue}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: isEditMode ? "space-between" : "flex-end",
        }}
      >
        {isEditMode && (
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            DELETE
          </Button>
        )}
        <Button
          onClick={isEditMode ? handleSave : handleInsert}
          disabled={isDisabled || !isAddressValid}
        >
          {isEditMode ? "Save Changes" : "Add Venue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueDialog;
